/**
 * TranscriptionPanel - Transcription Version List and Segments
 *
 * Displays transcription versions and their content:
 * - Version selector with model badges
 * - Timestamped segment list (for audio/video)
 * - Sync with media player
 * - Set preferred version
 * - Full text view for non-timestamped content
 *
 * @module @humanizer/studio/components/media/TranscriptionPanel
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaApi } from '../../hooks/useMediaApi';
import { createLogger } from '../../utils/debug';

const log = createLogger('TranscriptionPanel');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface TranscriptionPanelProps {
  /** Media ID to show transcriptions for */
  mediaId: string;
  /** Archive ID for context */
  archiveId?: string;
  /** Current playback time (for highlighting active segment) */
  currentTime?: number;
  /** Called when segment is clicked (for seeking) */
  onSegmentClick?: (startTime: number) => void;
  /** Called when transcription is requested */
  onRequestTranscription?: () => void;
  /** Called to open transcription text in workspace split view */
  onOpenInWorkspace?: (text: string, modelLabel: string) => void;
  /** Increment to trigger a reload of transcription versions */
  refreshKey?: number;
  /** Optional class name */
  className?: string;
}

/** Transcription version data */
interface TranscriptionVersion {
  id: string;
  versionNumber: number;
  type: 'audio' | 'ocr' | 'caption' | 'description' | 'manual';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  text?: string;
  segments?: TranscriptionSegment[];
  model: {
    id: string;
    provider: string;
    variant?: string;
  };
  isPreferred: boolean;
  completedAt?: Date;
  confidence?: number;
  language?: string;
  wordCount?: number;
  processingDurationMs?: number;
}

/** Type label config */
const TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  audio: { label: 'Audio', icon: '🎙️' },
  ocr: { label: 'OCR', icon: '📄' },
  caption: { label: 'Caption', icon: '🏷️' },
  description: { label: 'Description', icon: '📝' },
  manual: { label: 'Manual', icon: '✏️' },
};

/** Transcription segment */
interface TranscriptionSegment {
  id: number;
  start: number;
  end: number;
  text: string;
  confidence?: number;
  speaker?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/** Format timestamp */
function formatTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** Format date */
function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/** Get provider color class */
function getProviderClass(provider: string): string {
  switch (provider.toLowerCase()) {
    case 'ollama':
      return 'model-badge--ollama';
    case 'openai':
      return 'model-badge--openai';
    case 'anthropic':
      return 'model-badge--anthropic';
    case 'google':
      return 'model-badge--google';
    case 'cloudflare':
      return 'model-badge--cloudflare';
    default:
      return '';
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function TranscriptionPanel({
  mediaId,
  archiveId,
  currentTime,
  onSegmentClick,
  onRequestTranscription,
  onOpenInWorkspace,
  refreshKey,
  className = '',
}: TranscriptionPanelProps): React.ReactElement {
  // State
  const [versions, setVersions] = useState<TranscriptionVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const segmentsRef = useRef<HTMLDivElement>(null);
  const activeSegmentRef = useRef<HTMLButtonElement>(null);

  const mediaApi = useMediaApi();

  // Load versions
  useEffect(() => {
    const loadVersions = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await mediaApi.getTranscriptions(mediaId, archiveId);
        setVersions(
          result.versions
            .filter((v) => v.model)
            .map((v) => ({
              id: v.id,
              versionNumber: v.versionNumber,
              type: v.type as TranscriptionVersion['type'],
              status: v.status as TranscriptionVersion['status'],
              text: v.text,
              segments: v.segments as TranscriptionSegment[] | undefined,
              model: {
                id: v.model!.id,
                provider: v.model!.provider,
                variant: v.model!.variant,
              },
              isPreferred: v.isPreferred,
              completedAt: v.completedAt ? new Date(v.completedAt) : undefined,
              confidence: v.quality?.confidence,
              language: v.quality?.language,
              wordCount: v.quality?.wordCount,
              processingDurationMs: v.processingDurationMs,
            }))
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load transcriptions');
      } finally {
        setIsLoading(false);
      }
    };

    loadVersions();
  }, [mediaId, archiveId, mediaApi, refreshKey]);

  // Auto-select preferred version
  useEffect(() => {
    if (versions.length > 0 && !selectedVersionId) {
      const preferred = versions.find((v) => v.isPreferred) || versions[0];
      setSelectedVersionId(preferred.id);
    }
  }, [versions, selectedVersionId]);

  // Get selected version
  const selectedVersion = useMemo(
    () => versions.find((v) => v.id === selectedVersionId) ?? null,
    [versions, selectedVersionId]
  );

  // Find active segment based on current time
  const activeSegmentId = useMemo(() => {
    if (!selectedVersion?.segments || currentTime === undefined) return null;

    const segment = selectedVersion.segments.find(
      (s) => currentTime >= s.start && currentTime < s.end
    );
    return segment?.id ?? null;
  }, [selectedVersion, currentTime]);

  // Scroll active segment into view
  useEffect(() => {
    if (activeSegmentRef.current) {
      activeSegmentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [activeSegmentId]);

  // Handle segment click
  const handleSegmentClick = useCallback(
    (segment: TranscriptionSegment) => {
      onSegmentClick?.(segment.start);
    },
    [onSegmentClick]
  );

  // Handle version selection
  const handleVersionSelect = useCallback((versionId: string) => {
    setSelectedVersionId(versionId);
  }, []);

  // Handle set preferred
  const handleSetPreferred = useCallback(async (versionId: string) => {
    // Optimistic update
    setVersions((prev) =>
      prev.map((v) => ({
        ...v,
        isPreferred: v.id === versionId,
      }))
    );

    try {
      await mediaApi.setPreferredTranscription(versionId);
    } catch (err) {
      log.error('Failed to set preferred version:', err);
      // Reload to restore correct state
      mediaApi.getTranscriptions(mediaId, archiveId).then((result) => {
        setVersions(
          result.versions
            .filter((v) => v.model)
            .map((v) => ({
              id: v.id,
              versionNumber: v.versionNumber,
              type: v.type as TranscriptionVersion['type'],
              status: v.status as TranscriptionVersion['status'],
              text: v.text,
              segments: v.segments as TranscriptionSegment[] | undefined,
              model: { id: v.model!.id, provider: v.model!.provider, variant: v.model!.variant },
              isPreferred: v.isPreferred,
              completedAt: v.completedAt ? new Date(v.completedAt) : undefined,
              confidence: v.quality?.confidence,
              language: v.quality?.language,
              wordCount: v.quality?.wordCount,
              processingDurationMs: v.processingDurationMs,
            }))
        );
      }).catch(() => {});
    }
  }, [mediaApi, mediaId, archiveId]);

  // Loading state
  if (isLoading) {
    return (
      <div className={`transcription-panel transcription-panel--loading ${className}`}>
        <div className="transcription-panel__loading">
          <span className="transcription-panel__spinner" />
          Loading transcriptions...
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`transcription-panel transcription-panel--error ${className}`}>
        <div className="transcription-panel__error">
          <span aria-hidden="true">⚠️</span>
          {error}
        </div>
      </div>
    );
  }

  // Empty state
  if (versions.length === 0) {
    return (
      <div className={`transcription-panel transcription-panel--empty ${className}`}>
        <div className="transcription-panel__empty">
          <span aria-hidden="true">📝</span>
          <p>No transcriptions yet</p>
          {onRequestTranscription && (
            <button
              className="transcription-panel__request-btn"
              onClick={onRequestTranscription}
            >
              Create Transcription
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`transcription-panel ${className}`}>
      {/* Version Selector — grouped by type when multiple types exist */}
      <div className="transcription-versions">
        {(() => {
          const types = [...new Set(versions.map((v) => v.type))];
          const showTypeHeaders = types.length > 1;
          return types.map((type) => {
            const typeInfo = TYPE_LABELS[type] ?? { label: type, icon: '' };
            const typeVersions = versions.filter((v) => v.type === type);
            return (
              <div key={type} className="transcription-versions__group">
                {showTypeHeaders && (
                  <div className="transcription-versions__type-header">
                    <span aria-hidden="true">{typeInfo.icon}</span> {typeInfo.label}
                  </div>
                )}
                {typeVersions.map((version) => (
                  <button
                    key={version.id}
                    className={`transcription-version-chip ${selectedVersionId === version.id ? 'transcription-version-chip--selected' : ''} ${version.isPreferred ? 'transcription-version-chip--preferred' : ''}`}
                    onClick={() => handleVersionSelect(version.id)}
                    aria-pressed={selectedVersionId === version.id}
                  >
                    {!showTypeHeaders && (
                      <span className="transcription-version-chip__type-badge">
                        {typeInfo.icon}
                      </span>
                    )}
                    <span className={`model-badge ${getProviderClass(version.model.provider)}`}>
                      <span className="model-badge__provider">{version.model.provider}</span>
                      <span className="model-badge__name">{version.model.variant || version.model.id}</span>
                    </span>
                    {version.isPreferred && (
                      <span className="transcription-version-chip__preferred" aria-label="Preferred">
                        ★
                      </span>
                    )}
                    {version.completedAt && (
                      <span className="transcription-version-chip__date">
                        {formatDate(version.completedAt)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            );
          });
        })()}
      </div>

      {/* Version Actions */}
      {selectedVersion && (
        <div className="transcription-panel__version-info">
          <div className="transcription-panel__version-meta">
            <span className="transcription-panel__type">
              {(TYPE_LABELS[selectedVersion.type]?.icon ?? '') + ' ' + (TYPE_LABELS[selectedVersion.type]?.label ?? selectedVersion.type)}
            </span>
            {selectedVersion.language && (
              <span className="transcription-panel__language">{selectedVersion.language}</span>
            )}
            {selectedVersion.wordCount !== undefined && selectedVersion.wordCount > 0 && (
              <span className="transcription-panel__word-count">
                {selectedVersion.wordCount.toLocaleString()} words
              </span>
            )}
            {selectedVersion.processingDurationMs !== undefined && selectedVersion.processingDurationMs > 0 && (
              <span className="transcription-panel__duration">
                {selectedVersion.processingDurationMs < 1000
                  ? `${selectedVersion.processingDurationMs}ms`
                  : `${(selectedVersion.processingDurationMs / 1000).toFixed(1)}s`}
              </span>
            )}
            {selectedVersion.confidence !== undefined && (
              <span className="transcription-panel__confidence">
                {Math.round(selectedVersion.confidence * 100)}% confidence
              </span>
            )}
          </div>
          {!selectedVersion.isPreferred && (
            <button
              className="transcription-panel__prefer-btn"
              onClick={() => handleSetPreferred(selectedVersion.id)}
            >
              Set as Preferred
            </button>
          )}
          {onOpenInWorkspace && selectedVersion.text && (
            <button
              className="transcription-panel__open-btn"
              onClick={() => onOpenInWorkspace(
                selectedVersion.text!,
                selectedVersion.model.variant || selectedVersion.model.id,
              )}
            >
              Open in Workspace
            </button>
          )}
        </div>
      )}

      {/* Segments or Full Text */}
      {selectedVersion?.status === 'completed' && (
        <div ref={segmentsRef} className="transcription-segments">
          {selectedVersion.segments && selectedVersion.segments.length > 0 ? (
            // Timestamped segments
            selectedVersion.segments.map((segment) => {
              const isActive = segment.id === activeSegmentId;
              return (
                <button
                  key={segment.id}
                  ref={isActive ? activeSegmentRef : undefined}
                  className={`transcription-segment ${isActive ? 'transcription-segment--active' : ''}`}
                  onClick={() => handleSegmentClick(segment)}
                  aria-current={isActive ? 'true' : undefined}
                >
                  <span className="transcription-segment__timestamp">
                    {formatTimestamp(segment.start)}
                  </span>
                  <span className="transcription-segment__text">
                    {segment.speaker && (
                      <span className="transcription-segment__speaker">
                        {segment.speaker}:
                      </span>
                    )}
                    {segment.text}
                  </span>
                </button>
              );
            })
          ) : selectedVersion.text ? (
            // Full text (no timestamps)
            <div className="transcription-panel__full-text">
              {selectedVersion.text}
            </div>
          ) : (
            <div className="transcription-panel__no-content">
              No content available
            </div>
          )}
        </div>
      )}

      {/* Processing/Pending State */}
      {selectedVersion?.status === 'processing' && (
        <div className="transcription-panel__processing">
          <span className="transcription-panel__spinner" />
          Transcription in progress...
        </div>
      )}

      {selectedVersion?.status === 'pending' && (
        <div className="transcription-panel__pending">
          <span aria-hidden="true">⏳</span>
          Transcription queued
        </div>
      )}

      {selectedVersion?.status === 'failed' && (
        <div className="transcription-panel__failed">
          <span aria-hidden="true">❌</span>
          Transcription failed
          {onRequestTranscription && (
            <button
              className="transcription-panel__retry-btn"
              onClick={onRequestTranscription}
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default TranscriptionPanel;
